import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import EigooMark from "../components/EigooMark";
import { Loader2, CheckCircle2, AlertOctagon } from "lucide-react";

const ROLE_LANDING = {
    director: "/app/director",
    sports_admiral: "/app/sports-cmdr",
    td_commander: "/app/td-cmdr",
    founding_privateer: "/app/privateer",
    field_operative: "/app/operative",
    partner: "/app/dashboard",
};

/**
 * MagicLinkLand — handles the click-through from the email magic link.
 *
 * URL: `/magic/:token`. On mount:
 *   1. POST /auth/magic-link/verify with the token
 *   2. On success: refresh AuthContext, toast, navigate to role-specific landing
 *   3. On failure: render the failure state with a "request another" CTA
 */
export default function MagicLinkLand() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [state, setState] = useState("verifying"); // verifying | success | error
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.post("/auth/magic-link/verify", { token });
                if (cancelled) return;
                await refresh();
                setState("success");
                toast.success(`Welcome aboard, ${data.name || data.email}.`);
                setTimeout(() => {
                    navigate(ROLE_LANDING[data.role] || "/app/dashboard", { replace: true });
                }, 900);
            } catch (ex) {
                if (cancelled) return;
                setError(formatApiError(ex.response?.data?.detail) || ex.message || "Magic link verification failed");
                setState("error");
            }
        })();
        return () => { cancelled = true; };
    }, [token, navigate, refresh]);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6 grain-overlay relative">
            <div className="absolute inset-0 opacity-40 pointer-events-none">
                <div className="absolute top-[-30%] left-[-10%] w-[60%] h-[60%] rounded-full bg-[hsl(40_60%_45%/0.12)] blur-[120px]" />
                <div className="absolute bottom-[-30%] right-[-10%] w-[60%] h-[60%] rounded-full bg-[hsl(153_50%_25%/0.25)] blur-[120px]" />
            </div>
            <div className="relative w-full max-w-md glass rounded-2xl p-8 md:p-10 text-center" data-testid="magic-link-land">
                <EigooMark size="md" showWord={true} testid="auth-logo" className="mb-1 justify-center" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-3 mb-8">Sovereign Magic Link</div>

                {state === "verifying" && (
                    <div className="py-8" data-testid="magic-state-verifying">
                        <Loader2 size={32} className="mx-auto text-[hsl(45_85%_68%)] animate-spin" />
                        <div className="mt-5 font-heading text-xl">Verifying your link…</div>
                        <div className="text-xs text-muted-foreground mt-2">Burning the token, issuing your session.</div>
                    </div>
                )}
                {state === "success" && (
                    <div className="py-8" data-testid="magic-state-success">
                        <CheckCircle2 size={36} className="mx-auto text-[hsl(153_60%_70%)]" />
                        <div className="mt-5 font-heading text-2xl">Aboard.</div>
                        <div className="text-xs text-muted-foreground mt-2">Routing you to your hull…</div>
                    </div>
                )}
                {state === "error" && (
                    <div className="py-8" data-testid="magic-state-error">
                        <AlertOctagon size={36} className="mx-auto text-[hsl(350_70%_70%)]" />
                        <div className="mt-5 font-heading text-2xl">Link rejected</div>
                        <div className="text-sm text-[hsl(350_70%_80%)] mt-3">{error}</div>
                        <Link
                            to="/login?magic=1"
                            data-testid="magic-link-retry"
                            className="mt-6 inline-block px-6 py-2.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                        >
                            Request a new magic link
                        </Link>
                        <div className="mt-4 text-[11px] text-muted-foreground">
                            Magic links are single-use and expire after 15 minutes.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
